import { Controller } from "@hotwired/stimulus";
import {
    clearWorkflowStepNavCollapsedTooltips,
    refreshWorkflowStepNavCollapsedTooltips,
} from "../utils/workflow_step_nav_collapsed_tooltips";
import {
    applyWorkflowStepNavWidthPx,
    WORKFLOW_STEP_NAV_WIDTH_DEFAULT,
} from "../utils/workflow_step_nav_width_sync";

const STORAGE_KEY = "depozen:workflow-step-nav-layout";
const OPENED = "opened";
const COLLAPSED = "collapsed";
const XL_BREAKPOINT = 1280;

export default class extends Controller {
    static values = {
        collapseLabel: String,
        expandLabel: String,
    };

    connect() {
        window.addEventListener("storage", this.boundSyncLayoutFromStorage);
        this.element.addEventListener(
            "depozen:workflow-step-nav-collapse-from-resize",
            this.boundCollapseFromResize,
        );
        this.element.addEventListener(
            "depozen:workflow-step-nav-expand-from-resize",
            this.boundExpandFromResize,
        );

        const layout = this.readLayout();
        this.applyLayout(layout, { persist: false });

        refreshWorkflowStepNavCollapsedTooltips();
    }

    disconnect() {
        window.removeEventListener("storage", this.boundSyncLayoutFromStorage);
        this.element.removeEventListener(
            "depozen:workflow-step-nav-collapse-from-resize",
            this.boundCollapseFromResize,
        );
        this.element.removeEventListener(
            "depozen:workflow-step-nav-expand-from-resize",
            this.boundExpandFromResize,
        );
    }

    boundSyncLayoutFromStorage = (event) => this.syncLayoutFromStorage(event);
    boundCollapseFromResize = () => this.collapseFromResize();
    boundExpandFromResize = () => this.expandFromResize();

    toggle() {
        const next = this.currentLayout() === OPENED ? COLLAPSED : OPENED;

        if (next === OPENED) {
            applyWorkflowStepNavWidthPx(WORKFLOW_STEP_NAV_WIDTH_DEFAULT, { persist: true, instant: false });
            this.element.classList.remove("workflow-step-shell--nav-narrow");
        }

        this.applyLayout(next, { persist: true });

        if (next === COLLAPSED) {
            refreshWorkflowStepNavCollapsedTooltips();
        } else {
            clearWorkflowStepNavCollapsedTooltips();
        }
    }

    collapseFromResize() {
        if (this.currentLayout() === COLLAPSED) return;

        this.applyLayout(COLLAPSED, { persist: true });
        refreshWorkflowStepNavCollapsedTooltips();
    }

    expandFromResize() {
        if (this.currentLayout() === OPENED) return;

        this.applyLayout(OPENED, { persist: true });
        clearWorkflowStepNavCollapsedTooltips();
    }

    readLayout() {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === OPENED || stored === COLLAPSED) return stored;

        return window.matchMedia(`(min-width: ${XL_BREAKPOINT}px)`).matches ? OPENED : COLLAPSED;
    }

    currentLayout() {
        return this.element.dataset.workflowStepNavLayout === COLLAPSED ? COLLAPSED : OPENED;
    }

    applyLayout(layout, { persist }) {
        if (layout !== OPENED && layout !== COLLAPSED) return;

        this.element.dataset.workflowStepNavLayout = layout;
        document.documentElement.dataset.workflowStepNavLayout = layout;
        window.__DEPOZEN_WORKFLOW_STEP_NAV_LAYOUT__ = layout;

        if (layout === OPENED) {
            clearWorkflowStepNavCollapsedTooltips();
            this.element.classList.remove("workflow-step-shell--nav-narrow");
        }

        if (persist) {
            localStorage.setItem(STORAGE_KEY, layout);
        }

        this.updateToggleButton(layout);
        document.dispatchEvent(new CustomEvent("depozen:workflow-step-nav-layout-change"));
    }

    syncLayoutFromStorage(event) {
        if (event.key !== STORAGE_KEY) return;

        const layout =
            event.newValue === OPENED || event.newValue === COLLAPSED ? event.newValue : this.readLayout();
        if (layout === this.currentLayout()) return;

        this.applyLayout(layout, { persist: false });

        if (layout === COLLAPSED) {
            refreshWorkflowStepNavCollapsedTooltips();
        }
    }

    updateToggleButton(layout) {
        const button = this.element.querySelector(".workflow-step-nav-layout-toggle");
        if (!button) return;

        const collapsed = layout === COLLAPSED;
        const label = collapsed ? this.expandLabelValue : this.collapseLabelValue;
        button.setAttribute("aria-label", label);
    }
}
